import { useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { awsImage, setUserCity } from '@firsttable/functions';
import ScrollContainer from '@discover-components/availability/carousel/scrollContainer';
import useDragToScroll from '@discover-lib/useDragToScroll';
import { cn } from '@discover-lib/utils';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';
import { formatPD6630RestaurantCount, type PD6630Location } from './locations';

function LocationCard({ location }: { location: PD6630Location }) {
  const { track } = useAnalytics();
  const restaurantLabel = formatPD6630RestaurantCount(location.restaurantCount);
  const imageSrc = location.imagePath ? awsImage(location.imagePath, 430, 640) : null;

  return (
    <Link
      href={location.slug}
      draggable={false}
      onClick={() => {
        setUserCity(location.slug);
        track('link_clicked', {
          href: location.slug,
          link_type: 'location card',
          link_name: location.name,
          link_copy: location.name,
        });
      }}
      className="relative block h-[280px] w-[200px] shrink-0 overflow-hidden rounded no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ds-brand-700 sm:h-[320px] sm:w-[215px] sm:rounded-lg">
      {imageSrc ? (
        <Image
          src={imageSrc}
          alt=""
          fill
          sizes="(min-width: 784px) 215px, 200px"
          draggable={false}
          className="object-cover"
        />
      ) : (
        <span aria-hidden className="absolute inset-0 bg-ds-gray-light-800" />
      )}
      <span
        aria-hidden
        className="absolute inset-0 bg-gradient-to-b from-transparent from-[72%] to-black/60"
      />
      <span className="absolute inset-x-0 bottom-0 flex flex-col gap-1.5 px-4 pb-5 pt-4">
        <span className="line-clamp-2 text-md font-semibold text-white">{location.name}</span>
        {restaurantLabel ? (
          <span className="truncate text-xs font-semibold text-white">{restaurantLabel}</span>
        ) : null}
      </span>
    </Link>
  );
}

export default function PD6630AllLocations({ locations }: { locations: PD6630Location[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { isDragging, isScrollable, handleMouseDown, handleClickCapture } = useDragToScroll(
    scrollRef,
    { contentVersion: locations.length }
  );

  if (locations.length === 0) return null;

  return (
    <TrackProvider
      trackEvent="component_interaction"
      section="all locations"
      eventParams={{
        component_type: 'carousel',
        action: 'viewed',
      }}>
      <section className="flex flex-col gap-6" aria-label="All locations">
        <h2 className="m-0 px-4 font-sans text-md font-semibold text-ds-gray-light-950 sm:px-3 md:px-0">
          All locations
        </h2>
        <ScrollContainer
          ref={scrollRef}
          className={cn(
            'items-stretch gap-3 pl-4 pr-0 sm:gap-3 sm:px-3 md:px-0',
            isScrollable && isDragging && 'cursor-grabbing',
            isScrollable && !isDragging && 'cursor-grab'
          )}
          onMouseDownCapture={handleMouseDown}
          onClickCapture={handleClickCapture}>
          {locations.map((location) => (
            <LocationCard key={location.id} location={location} />
          ))}
        </ScrollContainer>
      </section>
    </TrackProvider>
  );
}
